import { SelectProps } from "@tfg/ui"
import { omit } from "lodash"
import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { CertificateStatusEnum } from "@tfg/types/schemas/enum"

import {
  FindCertificatesByOrganizationIdQueryParams,
  FindCertificatesByOrganizationIdQueryParamsExpiringCertificates,
} from "~/gen/api"
import { useCertificatesTableContext } from "~/pages/Brand/Certificates/provider/CertificatesTableContext"

export type FilterSelectProps = {
  key: keyof FindCertificatesByOrganizationIdQueryParams
  data: SelectProps["data"]
  placeholder: string
  onChange: SelectProps["onChange"]
  getValue: (
    filters: FindCertificatesByOrganizationIdQueryParams
  ) => string | undefined
  onClear: () => void
  hidden?: boolean
}

export const useFiltersSelectProps = (): FilterSelectProps[] => {
  const { certificateTypes, setFilters, unsetFilter, view } =
    useCertificatesTableContext()

  const { t } = useTranslation(["certificates", "common"])

  const statuses = useMemo(() => {
    return Object.values(
      CertificateStatusEnum.exclude(["NOT_UPLOADED"]).enum
    ).map((status) => ({
      label: t(`certificateStatus.${status}`),
      value: status,
    }))
  }, [t])

  const expiringCertificatesFilterSelectData = useMemo(() => {
    return [
      {
        value: "all",
        label: t("lists.filters.all"),
      },
      {
        value: "expiring",
        label: t("lists.filters.expiring"),
      },
    ]
  }, [t])

  const onStatusChange = useCallback(
    (value: string | null) => {
      if (!value) {
        setFilters((prev) => omit(prev, "status"))
        return
      }

      const certificateStatus = CertificateStatusEnum.safeParse(value)

      setFilters((prev) => ({
        ...prev,
        status: certificateStatus.success ? certificateStatus.data : undefined,
      }))
    },
    [setFilters]
  )

  const filtersSelectsProps = useMemo<FilterSelectProps[]>(() => {
    return [
      {
        key: "status",
        data: statuses,
        placeholder: t("lists.placeholders.certificateStatus"),
        onChange: onStatusChange,
        getValue: (filters) => filters.status ?? undefined,
        onClear: () => unsetFilter("status"),
      },
      {
        key: "certificateTypeId",
        data: certificateTypes,
        placeholder: t("lists.placeholders.certificateType"),
        onChange: (value) => {
          if (!value) return
          setFilters((prev) => ({
            ...prev,
            certificateTypeId: value,
          }))
        },
        getValue: (filters) => filters.certificateTypeId ?? undefined,
        onClear: () => unsetFilter("certificateTypeId"),
      },
      {
        key: "expiringCertificates",
        data: expiringCertificatesFilterSelectData,
        placeholder: t("lists.placeholders.expiringCertificates"),
        hidden: view === "transaction",
        onChange: (value) => {
          if (!value) return
          setFilters((prev) => ({
            ...prev,
            expiringCertificates: value as
              | FindCertificatesByOrganizationIdQueryParamsExpiringCertificates
              | undefined,
          }))
        },
        getValue: (filters) => filters.expiringCertificates ?? undefined,
        onClear: () => unsetFilter("expiringCertificates"),
      },
    ]
  }, [
    statuses,
    expiringCertificatesFilterSelectData,
    certificateTypes,
    onStatusChange,
    setFilters,
    unsetFilter,
    t,
    view,
  ])

  return filtersSelectsProps
}
